import React from 'react';
import { useSelector } from 'react-redux';
import Divider from 'housecall-ui/dist/DesignSystem/DataDisplay/Divider/Divider';
import Typography from 'housecall-ui/dist/DesignSystem/DataDisplay/Typography/Typography';
import Box from 'housecall-ui/dist/DesignSystem/Layout/Box/Box';
import Grid from 'housecall-ui/dist/DesignSystem/Layout/Grid/Grid';
import makeStyles from 'housecall-ui/dist/DesignSystem/Styles/makeStyles';
import { HCPTheme } from 'housecall-ui/dist/Theme/Theme';
import { formatCurrency } from 'utils/currency';

import SubtotalSection from 'components/PayWall/PlanSummaryTotal/SubtotalSection';
import { WHEN_TO_APPLY } from 'components/PayWall/Redux/Actions';
import { PayWallFullState } from 'components/PayWall/Redux/Reducer.type';
import { getActivationFeeToDisplay, getSkipSetupFee, getTotalDueToday } from 'components/PayWall/Redux/Selectors';

const useStyles = makeStyles((theme: HCPTheme) => ({
  strikeThroughText: {
    textDecoration: 'line-through',
    color: theme.palette.error.main,
  },
}));

function DueTodaySection() {
  const totalDueToday = useSelector((state: PayWallFullState) => getTotalDueToday(state));
  const activationFee = useSelector((state: PayWallFullState) => getActivationFeeToDisplay(state));
  const skipSetupFee = useSelector((state: PayWallFullState) => getSkipSetupFee(state));
  const salesTaxDueToday = useSelector(
    (state: PayWallFullState) => state.payWall.planSummaryPreview.sales_tax_due_today
  );
  const classes = useStyles();

  const dueToday = formatCurrency(Number(totalDueToday) + Number(salesTaxDueToday));
  const totalBeforeDiscount = formatCurrency(Number(totalDueToday) + Number(activationFee) + Number(salesTaxDueToday));

  return (
    <>
      <Grid container>
        <Grid item xs={6}>
          <Typography variant="subtitle1">Amount due today</Typography>
        </Grid>

        <Grid item xs={6}>
          {skipSetupFee && (
            <Typography
              variant="subtitle1"
              align="right"
              className={classes.strikeThroughText}
              data-testid="total-before-discount"
            >
              {totalBeforeDiscount}
            </Typography>
          )}
          <Typography variant="subtitle1" align="right" data-testid="amount-due-today">
            {dueToday}
          </Typography>
        </Grid>
      </Grid>
      <Box my={2}>
        <Divider />
      </Box>
    </>
  );
}

function ProratedFeaturesNotice() {
  const whenToApplyChange = useSelector((state: PayWallFullState) => state.payWall.planSummaryPreview.when_to_apply);

  if (whenToApplyChange !== WHEN_TO_APPLY.NOW_ADDONS_ONLY) {
    return null;
  }

  return (
    <Grid container>
      <Grid item>
        <Typography variant="body2" data-testid="prorated-features-notice">
          This is the calculated total for your new Add-ons based <br />
          on the amount of time left until your renewal date.
        </Typography>
      </Grid>
    </Grid>
  );
}

function DueNextBillingCycle() {
  const recurringSalesTax = useSelector(
    (state: PayWallFullState) => state.payWall.planSummaryPreview.recurring_sales_tax
  );
  const renewalDate = useSelector((state: PayWallFullState) => state.payWall.planSummaryPreview.renewal_date);
  const recurringAmount = useSelector((state: PayWallFullState) => state.payWall.planSummaryPreview.recurring_amount);

  return (
    <Box>
      <Typography variant="body2">
        Amount due next renewal {formatCurrency(parseFloat(recurringAmount) + parseFloat(recurringSalesTax))}
      </Typography>
      <Typography variant="caption" data-testid="amount-due-renewal-upgrade">
        {renewalDate}
      </Typography>
    </Box>
  );
}

export default function UpgradeTotal() {
  return (
    <>
      <SubtotalSection />
      <DueTodaySection />
      <ProratedFeaturesNotice />
      <DueNextBillingCycle />
    </>
  );
}
